// useAiAvailability() — the one place every screen with an AI control checks
// whether AI is actually usable right now (docs/HMS_V4_AI_PROVIDER_MANAGEMENT.md
// §4). AI-off must mean no control is even RENDERED, not shown disabled — a
// greyed-out mic/chat button still tells a doctor "AI exists here", which is
// not what a hospital that switched it off asked for.
//
// Tenant-wide (GET /api/ai-settings is readable by any staff member, and the
// switch applies hospital-wide) — keyed with tenantKey(), never branchKey().
// staleTime 60s + refetch on window focus: saving the switch elsewhere
// invalidates this key (see AISettingsTab's save), so a screen left open in
// another tab still catches up within a minute or on refocus.
import { useQuery } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import { getAiSettings } from '@/api';

export interface AiAvailability {
  /** Still loading the very first read — callers must render NO AI control yet. */
  loading: boolean;
  aiEnabled: boolean;
  memoryEnabled: boolean;
  suggestionsEnabled: boolean;
}

export function useAiAvailability(): AiAvailability {
  const q = useQuery({
    queryKey: keys.config.aiSettings(),
    queryFn: getAiSettings,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  if (!q.data) {
    // While loading (or on a fetch error), AI controls must not flash on and
    // then vanish — treat as unavailable until a definite answer arrives.
    return { loading: q.isLoading, aiEnabled: false, memoryEnabled: false, suggestionsEnabled: false };
  }

  return {
    loading: false,
    aiEnabled: !!q.data.ai_processing_enabled,
    memoryEnabled: !!q.data.ai_processing_enabled, // doctor_memory_enabled rides on the same off-switch; see AiOnly below for the finer-grained Admin screen exemption
    suggestionsEnabled: q.data.ai_suggestions_enabled !== false,
  };
}
